import { Alert, Box, Card, CardContent, CircularProgress, Container, Link, Typography } from '@mui/material';
import { usePostHog } from '@posthog/react';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { capture } from '../analytics/posthog';
import SurveyForm from '../components/survey/SurveyForm';
import ApiClient from '../config/ApiClient';
import { ProgressSurveyContext, ProgressSurveySubmission } from '../types/survey';

const ProgressSurvey: React.FC = () => {
  const { id, weekNum } = useParams<{ id: string; weekNum: string }>();
  const posthog = usePostHog();
  const [context, setContext] = useState<ProgressSurveyContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchContext = async () => {
      if (!id || !weekNum) {
        setError('Invalid lesson plan or week');
        setLoading(false);
        return;
      }
      const sn = parseInt(weekNum, 10);
      if (Number.isNaN(sn) || sn < 1 || sn > 12) {
        setError('Week must be between 1 and 12');
        setLoading(false);
        return;
      }
      try {
        const response = await ApiClient.get<ProgressSurveyContext>(`/api/lesson-plan/${id}/week/${weekNum}/progress-survey-context/`);
        if (response.success && response.data) {
          setContext(response.data as ProgressSurveyContext);
        } else {
          setError((response as any).error || 'Unable to load survey. You may not have access.');
        }
      } catch (err: any) {
        setError('Failed to load survey. Please check your link and try again.');
        console.error('Error fetching progress survey context:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContext();
  }, [id, weekNum]);

  const handleSubmit = async (data: ProgressSurveySubmission) => {
    setSubmitting(true);
    setSubmitError(null);

    const lessonPlanIdNum = id ? parseInt(id, 10) : NaN;
    const weekNumber = weekNum ? parseInt(weekNum, 10) : NaN;
    capture(posthog, "progress_survey_submitted", { lesson_plan_id: lessonPlanIdNum, week_number: weekNumber });

    try {

      const response = await ApiClient.post('/api/survey-responses/', data);
      if (response.success) {
        setSuccess(true);
      } else {
        setSubmitError((response as any).error || 'Failed to submit survey. Please try again.');
      }
    } catch (err: any) {
      setSubmitError('An error occurred while submitting the survey. Please try again.');
      console.error('Error submitting survey:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !context) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Survey not found'}</Alert>
        <Typography variant="body1">If you believe this is an error, please contact support or check that you have access to this lesson plan.</Typography>
      </Container>
    );
  }

  if (success) {
    const continueHref = id ? `/lesson-plan/${id}` : '/dashboard';
    const continueLabel = id ? 'Return to lesson plan' : 'Go to dashboard';
    return (
      <Container maxWidth="md" sx={{ py: 4 }} data-testid="progress-survey">
        <Card data-testid="progress-survey-success">
          <CardContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>Thank you for completing the survey!</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your responses have been submitted successfully.
              </Typography>
              <Link component={RouterLink} to={continueHref} variant="body2" fontWeight={600}>
                {continueLabel}
              </Link>
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="progress-survey">
      <SurveyForm
        context={context}
        lessonPlanId={parseInt(id!, 10)}
        respondentType={context.respondent_type}
        onSubmit={handleSubmit}
        loading={submitting}
        error={submitError}
      />
    </Container>
  );
};

export default ProgressSurvey;
